import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { addOnZ, AddOnFormValues } from '@libs/schemas/src/add-on';

interface AddOnFormProps {
  initialValues?: Partial<AddOnFormValues>;
  onSubmit: (values: AddOnFormValues) => void;
  isEditing?: boolean;
  onStateChange?: (state: { isValid: boolean }) => void;
}

export const AddOnForm: React.FC<AddOnFormProps> = ({
  initialValues,
  onSubmit,
  isEditing = false,
  onStateChange,
}) => {
  const form = useForm<AddOnFormValues>({
    resolver: zodResolver(addOnZ),
    defaultValues: initialValues || {
      title: '',
      description: '',
      priceInCents: '',
      archived: false,
      customerVisible: true,
    },
  });

  const { isValid } = useFormState({ control: form.control });

  useEffect(() => {
    onStateChange?.({ isValid });
  }, [isValid, onStateChange]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-on-form">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-text-light">Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-text-light">Description</FormLabel>
              <FormControl>
                <Input placeholder="Enter description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="priceInCents"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-text-light">Price ($)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter price in dollars"
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="customerVisible"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-text-light">Customer Visible</FormLabel>
                <p className="text-xs text-muted">
                  Can be seen and selected by customers during checkout if enabled
                </p>
              </div>
            </FormItem>
          )}
        />
        {isEditing && (
          <FormField
            control={form.control}
            name="archived"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-text-light">Archived</FormLabel>
                </div>
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
};
